import { BackgroundBlur, BackgroundBlurMode, Language, Layout, VoiceAssistantProvider } from 'intermind';
import { create, StateCreator } from 'zustand';
import { createJSONStorage, persist, PersistOptions } from 'zustand/middleware';

export interface SettingsStore {
  // Flag to track initialization state
  isInitialized: boolean;
  setInitialized: (initialized: boolean) => void;

  microphoneId: string | null;
  setMicrophoneId: (microphoneId: string | null) => void;

  cameraId: string | null;
  setCameraId: (cameraId: string | null) => void;

  speakerId: string | null;
  setSpeakerId: (speakerId: string | null) => void;

  layout: Layout;
  setLayout: (layoutType: Layout) => void;

  voiceAssistantProvider: VoiceAssistantProvider;
  setVoiceAssistantProvider: (voiceAssistantProvider: VoiceAssistantProvider) => void;

  language: Language;
  setLanguage: (language: Language) => void;

  originalVolumeLevel: number; // 0 to 100 percentage
  setOriginalVolumeLevel: (originalVolumeLevel: number) => void;

  backgroundBlur: BackgroundBlur;
  setBackgroundBlur: (backgroundBlur: BackgroundBlur) => void;
  toggleBackgroundBlur: () => void;

  backgroundBlurMode: BackgroundBlurMode;
  setBackgroundBlurMode: (backgroundBlurMode: BackgroundBlurMode) => void;

  newsletterSubscription: boolean;
  setNewsletterSubscription: (subscribe: boolean) => void;

  promotionsSubscription: boolean;
  setPromotionsSubscription: (subscribe: boolean) => void;

  showMediaStreamStatistics: boolean;
  setShowMediaStreamStatistics: (show: boolean) => void;

  leaveCallAutomaticallyWhenAlone: boolean;
  setLeaveCallAutomaticallyWhenAlone: (leaveCallAutomaticallyWhenAlone: boolean) => void;
}

// Define a type for the persisted part of the state
type PersistedState = Omit<SettingsStore, 'isInitialized'>;

type SettingsStorePersist = (
  config: StateCreator<SettingsStore>,
  options: PersistOptions<SettingsStore, PersistedState>
) => StateCreator<SettingsStore>;

export const useSettingsStore = create<SettingsStore>(
  (persist as SettingsStorePersist)(
    (set) => ({
      // Initialize flag
      isInitialized: false,
      setInitialized: (initialized: boolean) => set({ isInitialized: initialized }),

      microphoneId: null,
      setMicrophoneId: (microphoneId: string | null) => set({ microphoneId: microphoneId }),

      cameraId: null,
      setCameraId: (cameraId: string | null) => set({ cameraId: cameraId }),

      speakerId: null,
      setSpeakerId: (speakerId: string | null) => set({ speakerId: speakerId }),

      layout: Layout.GRID,
      setLayout: (layoutType: Layout) => set({ layout: layoutType ?? Layout.GRID }),

      voiceAssistantProvider: VoiceAssistantProvider.MIND,
      setVoiceAssistantProvider: (voiceAssistantProvider: VoiceAssistantProvider) =>
        set({ voiceAssistantProvider: voiceAssistantProvider ?? VoiceAssistantProvider.MIND }),

      language: Language.ENGLISH,
      setLanguage: (language: Language) => set({ language }),

      originalVolumeLevel: 10,
      setOriginalVolumeLevel: (originalVolumeLevel: number) => set({ originalVolumeLevel }),

      backgroundBlur: BackgroundBlur.DISABLED,
      setBackgroundBlur: (backgroundBlur: BackgroundBlur) =>
        set({ backgroundBlur: backgroundBlur ?? BackgroundBlur.DISABLED }),

      backgroundBlurMode: BackgroundBlurMode.REGULAR,
      setBackgroundBlurMode: (backgroundBlurMode: BackgroundBlurMode) =>
        set({ backgroundBlurMode: backgroundBlurMode ?? BackgroundBlurMode.REGULAR }),

      toggleBackgroundBlur: () =>
        set((state) => ({
          backgroundBlur:
            state.backgroundBlur === BackgroundBlur.ENABLED ? BackgroundBlur.DISABLED : BackgroundBlur.ENABLED,
        })),

      newsletterSubscription: true,
      setNewsletterSubscription: (subscribe: boolean) => set({ newsletterSubscription: subscribe }),

      promotionsSubscription: true,
      setPromotionsSubscription: (subscribe: boolean) => set({ promotionsSubscription: subscribe }),

      showMediaStreamStatistics: false,
      setShowMediaStreamStatistics: (show: boolean) => set({ showMediaStreamStatistics: show }),

      leaveCallAutomaticallyWhenAlone: true,
      setLeaveCallAutomaticallyWhenAlone: (leaveCallAutomaticallyWhenAlone: boolean) =>
        set({ leaveCallAutomaticallyWhenAlone }),
    }),
    {
      name: 'settings',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => {
        // Exclude isInitialized from persistence
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { isInitialized, ...rest } = state;
        return rest;
      },
    }
  )
);
